import ImageIcon from '@mui/icons-material/Image'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import GridViewIcon from '@mui/icons-material/GridView'
import StarIcon from '@mui/icons-material/Star'
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark'
import { createUniqueSectionKey } from '../../utils/themeSectionSelectors'

const clone = (value) => JSON.parse(JSON.stringify(value))

export const createSectionId = () => `s_${Math.random().toString(36).slice(2, 10)}`

const defaults = {
  heroBanner: {
    slides: [],
    autoplay: true,
    interval: 5000,
    spacingTop: 0,
    spacingBottom: 40,
    cssKey: '',
  },
  contentBlock: {
    header: '',
    text: '',
    image: '',
    imageAlt: '',
    spacingTop: 0,
    spacingBottom: 40,
    cssKey: '',
  },
  collection: {
    sourceType: 'category',
    sourceId: null,
    sourceName: '',
    sourceSlug: '',
    title: '',
    columns: 2,
    rows: 2,
    showMoreLink: true,
    spacingTop: 0,
    spacingBottom: 40,
    cssKey: '',
  },
  featuredProduct: {
    productId: null,
    layout: 'side-by-side',
    spacingTop: 0,
    spacingBottom: 40,
    cssKey: '',
  },
  productCatalog: {
    columns: 4,
    rows: 3,
    sortBy: 'date_desc',
    showSorting: true,
    showLoadMore: true,
    spacingTop: 0,
    spacingBottom: 40,
    cssKey: '',
  },
  categoryTiles: {
    title: '',
    columns: 2,
    items: [],
    gap: 16,
    paddingX: 0,
    spacingTop: 0,
    spacingBottom: 40,
    cssKey: '',
  },
}

const createBaseSection = (type, existingSections = []) => ({
  id: createSectionId(),
  key: createUniqueSectionKey('', type, existingSections),
  type,
  visible: true,
  settings: clone(defaults[type]),
})

export const SECTION_REGISTRY = {
  heroBanner: {
    type: 'heroBanner',
    label: 'Hero Banner',
    description: 'Full-width image or slider with a headline and call-to-action',
    Icon: ImageIcon,
    createDefault: (existingSections = []) => createBaseSection('heroBanner', existingSections),
  },
  contentBlock: {
    type: 'contentBlock',
    label: 'Content Block',
    description: 'Free-form text and an image — good for intros, announcements, or stories',
    Icon: TextFieldsIcon,
    createDefault: (existingSections = []) => createBaseSection('contentBlock', existingSections),
  },
  collection: {
    type: 'collection',
    label: 'Collection',
    description: 'Show a slice of products from a single category or collection',
    Icon: ViewModuleIcon,
    createDefault: (existingSections = []) => createBaseSection('collection', existingSections),
  },
  featuredProduct: {
    type: 'featuredProduct',
    label: 'Featured Product',
    description: 'Highlight a single product with its description and image side by side',
    Icon: StarIcon,
    createDefault: (existingSections = []) => createBaseSection('featuredProduct', existingSections),
  },
  productCatalog: {
    type: 'productCatalog',
    label: 'Product Catalog',
    description: 'Full product catalog with sorting and load-more',
    Icon: GridViewIcon,
    createDefault: (existingSections = []) => createBaseSection('productCatalog', existingSections),
  },
  categoryTiles: {
    type: 'categoryTiles',
    label: 'Category Tiles',
    description: 'Clickable cards linking to categories or collections',
    Icon: CollectionsBookmarkIcon,
    createDefault: (existingSections = []) => createBaseSection('categoryTiles', existingSections),
  },
}

export const SECTION_TYPES = Object.values(SECTION_REGISTRY).map(({ type, label, description, Icon }) => ({
  type,
  label,
  description,
  Icon,
}))

export const getSectionDefinition = (type) => SECTION_REGISTRY[type]

export const getSectionLabel = (type) => SECTION_REGISTRY[type]?.label || type

export const getSectionIcon = (type) => SECTION_REGISTRY[type]?.Icon || ViewModuleIcon

export const createSectionByType = (type, existingSections = []) => {
  const definition = getSectionDefinition(type)
  return definition ? definition.createDefault(existingSections) : null
}

export const duplicateSection = (section, existingSections = []) => ({
  ...clone(section),
  id: createSectionId(),
  key: createUniqueSectionKey(section?.key || '', section?.type, existingSections),
})
